import React, { useState, useEffect } from 'react';
import '../styles/components/quickStartGuide.css';

interface QuickStartGuideProps {
  onComplete: () => void;
  onSkip: () => void;
}

interface SlideContent {
  icon: string;
  title: string;
  description: string;
  tip?: string;
}

const slides: SlideContent[] = [
  {
    icon: '👋',
    title: 'Welcome to Wandering Sock!',
    description: 'Your personal inventory assistant. Keep track of everything you own, from groceries to collectibles.',
    tip: 'We\'ve created a 🍋\u00A0Grocery collection with 12 starter items for you!'
  },
  {
    icon: '📦',
    title: 'Try Your First Collection',
    description: 'Tap "All Items" at the top to open the collection selector, then choose 🍋\u00A0Grocery collection to see your starter items.',
    tip: 'Collections help you organize items by category - Pantry, Tools, Closet, etc.'
  },
  {
    icon: '📸',
    title: 'Scan Items Instantly',
    description: 'Take a photo and our AI will identify items automatically. No more manual data entry!',
    tip: 'Works great for groceries, receipts, and household items'
  },
  {
    icon: '👥',
    title: 'Share with Family',
    description: 'Share collections with family members. Everyone stays in sync - no more duplicate purchases!',
    tip: 'Perfect for shared grocery lists'
  },
  {
    icon: '🛒',
    title: 'Shopping Lists',
    description: 'Create a Shopping List to track what you have and what you need. Use Manage mode at home, Shop mode at the store.',
    tip: 'Your 🍋\u00A0Grocery collection is ready to try!'
  }
];

const QuickStartGuide: React.FC<QuickStartGuideProps> = ({ onComplete, onSkip }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const isLastSlide = currentSlide === slides.length - 1;
  const isFirstSlide = currentSlide === 0;

  // Minimum swipe distance
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && !isLastSlide) {
      setCurrentSlide(prev => prev + 1);
    }
    if (isRightSwipe && !isFirstSlide) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const handleNext = () => {
    if (isLastSlide) {
      onComplete();
    } else {
      setCurrentSlide(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstSlide) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const handleDotClick = (index: number) => {
    setCurrentSlide(index);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && !isLastSlide) {
        setCurrentSlide(prev => prev + 1);
      } else if (e.key === 'ArrowLeft' && !isFirstSlide) {
        setCurrentSlide(prev => prev - 1);
      } else if (e.key === 'Escape') {
        onSkip();
      } else if (e.key === 'Enter' && isLastSlide) {
        onComplete();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLastSlide, isFirstSlide, onComplete, onSkip]);

  const slide = slides[currentSlide];

  return (
    <div className="quick-start-overlay">
      <div 
        className="quick-start-container"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Skip button */}
        <button className="quick-start-skip" onClick={onSkip}>
          Skip
        </button>

        {/* Slide content */}
        <div className="quick-start-content">
          <div className="quick-start-icon">{slide.icon}</div>
          <h1 className="quick-start-title">{slide.title}</h1>
          <p className="quick-start-description">{slide.description}</p>
          {slide.tip && (
            <div className="quick-start-tip">
              <span className="tip-icon">💡</span>
              <span className="tip-text">{slide.tip}</span>
            </div>
          )}
        </div>

        {/* Dots indicator */}
        <div className="quick-start-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`quick-start-dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Navigation buttons */}
        <div className="quick-start-nav">
          <button 
            className="quick-start-nav-btn prev"
            onClick={handlePrev}
            disabled={isFirstSlide}
            style={{ opacity: isFirstSlide ? 0 : 1 }}
          >
            ← Back
          </button>
          <button 
            className="quick-start-nav-btn next primary"
            onClick={handleNext}
          >
            {isLastSlide ? 'Get Started!' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickStartGuide;
